import { EtfEyebrow } from "@/components/layout/EtfEyebrow";
import { EtfNewsletter } from "@/components/layout/EtfNewsletter";
import { type BlogPostView, useBlogPosts } from "@/hooks/use-blog-posts";
import { blogPostPath } from "@/lib/fund-paths";
import { JsonLd, Seo, collectionPageLd } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

// BlogListPage — "Insights".
// Ported from dane/frontend/app/etf-website/blog/page.tsx. Now backed by the
// public API (`useBlogPosts` → `/api/data/posts`), which returns only
// published + env-live posts. Categories are derived from the live payload;
// loading + empty states render gracefully (no white-screen).

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function readTimeLabel(minutes: number): string {
  return `${minutes} min read`;
}

export function BlogListPage() {
  const [activeCategory, setActiveCategory] = useState<string>("All Topics");

  const { data, isLoading } = useBlogPosts();
  const posts = useMemo<BlogPostView[]>(() => data ?? [], [data]);

  const categories = useMemo(() => {
    const set = new Set<string>(["All Topics"]);
    for (const p of posts) set.add(p.category);
    return Array.from(set);
  }, [posts]);

  // First post flagged featured, else the newest (API returns newest-first).
  const featuredPost = useMemo(() => posts.find((p) => p.featured) ?? posts[0], [posts]);

  const filteredPosts = useMemo(() => {
    if (activeCategory === "All Topics") return posts;
    return posts.filter((p) => p.category === activeCategory);
  }, [posts, activeCategory]);

  return (
    <>
      <Seo
        title="Insights & Research"
        description="Market commentary, fund deep-dives, and research from the Corgi Funds team on thematic, leveraged, buffer, and fixed-income ETF strategies."
        canonicalPath="/blog"
      />
      <JsonLd
        data={collectionPageLd({
          name: "Insights & Research — Corgi Funds",
          description:
            "Market commentary, fund deep-dives, and research from the Corgi Funds team.",
          path: "/blog",
          items: posts.map((p) => ({ name: p.title, path: blogPostPath(p.slug) })),
        })}
      />
      {/* ── Page Header ── */}
      <section className="grid grid-cols-2 items-end gap-20 border-b border-[var(--etf-border)] px-10 pb-[60px] pt-[72px] max-md:grid-cols-1 max-md:gap-7 max-md:px-5 max-md:pb-10 max-md:pt-12 max-[480px]:px-4 max-[480px]:pb-8 max-[480px]:pt-9">
        <div>
          <EtfEyebrow className="mb-5">Insights &amp; Research</EtfEyebrow>
          <h1 className="font-[family-name:var(--font-f37bolton)] text-[clamp(56px,7vw,96px)] font-normal leading-none tracking-[-0.02em] text-[var(--etf-white)] max-md:text-[clamp(44px,10vw,72px)]">
            Insights.
          </h1>
        </div>
        <div>
          <p className="mb-7 text-base font-light italic leading-[1.75] text-[var(--etf-white2)]">
            Our team's thinking on markets, technology, and the structural forces shaping long-term
            investment opportunities.
          </p>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={cn(
                  "cursor-pointer border border-[var(--etf-border)] bg-transparent px-3.5 py-1.5 font-[family-name:var(--font-instrument)] text-xs tracking-[0.08em] text-[var(--etf-white3)] transition-all duration-150 hover:text-[var(--etf-white2)]",
                  activeCategory === cat &&
                    "border-[var(--etf-orange)] bg-[rgba(255,85,0,0.06)] text-[var(--etf-orange)]",
                )}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Article ── */}
      {activeCategory === "All Topics" && featuredPost && (
        <Link
          to={blogPostPath(featuredPost.slug)}
          className="group grid grid-cols-2 border-b border-[var(--etf-border)] text-inherit no-underline transition-[background] duration-150 hover:bg-[rgba(255,255,255,0.015)] max-md:grid-cols-1"
        >
          <div className="relative flex min-h-[400px] items-end overflow-hidden border-r border-[var(--etf-border)] bg-[var(--etf-black2)] p-8 max-md:min-h-[240px] max-md:border-b max-md:border-r-0 max-md:border-[var(--etf-border)]">
            <div className="absolute inset-0 bg-[linear-gradient(135deg,#111_0%,#0d1a10_50%,#111_100%)] [[data-theme='light']_&]:bg-[linear-gradient(135deg,#e3e0dc_0%,#d9e0db_50%,#e3e0dc_100%)]">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,85,0,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,85,0,0.04)_1px,transparent_1px)] bg-[length:32px_32px]" />
            </div>
            <span className="relative z-[2] border border-[rgba(255,85,0,0.4)] bg-[rgba(0,0,0,0.6)] px-2.5 py-1 font-[family-name:var(--font-instrument)] text-xs uppercase tracking-[0.16em] text-[var(--etf-orange)]">
              Featured Article
            </span>
          </div>
          <div className="flex flex-col justify-center px-12 py-[52px] max-md:px-6 max-md:py-8 max-[480px]:px-4 max-[480px]:py-6">
            <div className="mb-5 flex items-center gap-4">
              <span className="font-[family-name:var(--font-instrument)] text-xs uppercase tracking-[0.12em] text-[var(--etf-orange)]">
                {featuredPost.category}
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.06em] text-[var(--etf-white3)]">
                {formatDate(featuredPost.publishedAt)}
              </span>
              <span className="ml-auto font-[family-name:var(--font-instrument)] text-xs tracking-[0.06em] text-[var(--etf-white3)]">
                {readTimeLabel(featuredPost.readTimeMinutes)}
              </span>
            </div>
            <h2 className="mb-4 font-[family-name:var(--font-f37bolton)] text-[clamp(32px,4vw,52px)] leading-none tracking-[-0.02em] text-[var(--etf-white)] max-[480px]:text-[clamp(24px,8vw,36px)]">
              {featuredPost.title}
            </h2>
            <p className="mb-7 text-[15px] font-light italic leading-[1.7] text-[var(--etf-white2)]">
              {featuredPost.excerpt}
            </p>
            <div className="flex items-center gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[var(--etf-border)] bg-[var(--etf-black3)] font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)]">
                {featuredPost.authorInitials}
              </div>
              <div>
                <div className="font-[family-name:var(--font-instrument)] text-sm tracking-[0.04em] text-[var(--etf-white2)]">
                  {featuredPost.author}
                </div>
                <div className="mt-px font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)]">
                  {featuredPost.authorTitle} · Corgi Funds
                </div>
              </div>
              <span className="ml-auto inline-flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-sm tracking-[0.06em] text-[var(--etf-orange)] no-underline transition-[gap] duration-200 group-hover:gap-2.5">
                Read Article →
              </span>
            </div>
          </div>
        </Link>
      )}

      {/* ── Articles Grid ── */}
      <div className="px-10 py-12 max-md:px-5 max-md:py-9 max-[480px]:px-4 max-[480px]:py-7">
        <div className="mb-7 flex items-center gap-4 font-[family-name:var(--font-instrument)] text-xs uppercase tracking-[0.16em] text-[var(--etf-white3)] after:h-px after:flex-1 after:bg-[var(--etf-border)]">
          Latest Articles
        </div>

        {isLoading ? (
          <div className="mb-12 py-16 text-center font-[family-name:var(--font-instrument)] text-sm tracking-[0.06em] text-[var(--etf-white3)]">
            Loading articles…
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="mb-12 border border-[var(--etf-border)] py-16 text-center">
            <div className="font-[family-name:var(--font-instrument)] text-sm tracking-[0.06em] text-[var(--etf-white3)]">
              No articles published yet. Check back soon.
            </div>
          </div>
        ) : (
          <div className="mb-12 grid grid-cols-3 gap-px border border-[var(--etf-border)] bg-[var(--etf-border)] max-lg:grid-cols-2 max-md:grid-cols-1">
            {filteredPosts.map((post) => (
              <Link
                key={post.slug}
                to={blogPostPath(post.slug)}
                className="group/card flex min-h-[280px] flex-col bg-[var(--etf-black)] p-7 text-inherit no-underline transition-[background] duration-150 hover:bg-[var(--etf-black2)]"
              >
                <div className="mb-3.5 font-[family-name:var(--font-instrument)] text-xs uppercase tracking-[0.12em] text-[var(--etf-orange)]">
                  {post.category} · {formatDate(post.publishedAt)}
                </div>
                <div className="mb-3 flex-1 font-[family-name:var(--font-f37bolton)] text-2xl leading-none tracking-[-0.02em] text-[var(--etf-white)]">
                  {post.title}
                </div>
                <div className="mb-5 line-clamp-3 text-[13px] font-light italic leading-[1.65] text-[var(--etf-white3)]">
                  {post.excerpt}
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-[var(--etf-border2)] pt-4">
                  <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.04em] text-[var(--etf-white3)]">
                    {post.author} · {readTimeLabel(post.readTimeMinutes)}
                  </div>
                  <div className="translate-x-[-6px] font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] opacity-0 transition-all duration-200 group-hover/card:translate-x-0 group-hover/card:opacity-100">
                    →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* ── Newsletter ── */}
        <EtfNewsletter className="justify-start border border-[var(--etf-border)] p-12 mb-12 max-md:p-5 max-md:gap-6" />
      </div>
    </>
  );
}
